/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import type { RequestConfig, ResponseErrorConfig } from '../.kubb/client'
import type {
  ListOrganicTrafficSeriesOptions,
  ListOrganicTrafficSeriesStatus200,
  ListOrganicTrafficSeriesStatus400,
  ListOrganicTrafficSeriesStatus403,
  ListOrganicTrafficSeriesStatus500,
} from '../types/ListOrganicTrafficSeries'
import type {
  QueryKey,
  QueryClient,
  QueryObserverOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { listOrganicTrafficSeries } from '../clients/listOrganicTrafficSeries'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const listOrganicTrafficSeriesQueryKey = ({
  query,
}: Omit<ListOrganicTrafficSeriesOptions, 'headers'>) =>
  [{ url: '/v1/bots/organic-traffic/series' }, ...(query ? [query] : [])] as const

type ListOrganicTrafficSeriesQueryKey = ReturnType<typeof listOrganicTrafficSeriesQueryKey>

export function listOrganicTrafficSeriesQueryOptions(
  { query }: ListOrganicTrafficSeriesOptions,
  config: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>> = {},
) {
  const queryKey = listOrganicTrafficSeriesQueryKey({ query })
  return queryOptions<
    ListOrganicTrafficSeriesStatus200,
    ResponseErrorConfig<
      | ListOrganicTrafficSeriesStatus400
      | ListOrganicTrafficSeriesStatus403
      | ListOrganicTrafficSeriesStatus500
    >,
    ListOrganicTrafficSeriesStatus200,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      return listOrganicTrafficSeries({
        ...config,
        query,
        signal: config.signal ?? signal,
        throwOnError: true,
      }).unwrap()
    },
  })
}

/**
 * @description Admin only. One point per interval, oldest first. The traffic figures are summed over the metric buckets, which do not overlap. The assessment figures are averaged over the recorded evaluations, which are overlapping sliding windows and so must never be summed: a trade falls in every window that covers it.
 * @summary Report one order book's organic activity over time
 * {@link /v1/bots/organic-traffic/series}
 */
export function useListOrganicTrafficSeries<
  TData = ListOrganicTrafficSeriesStatus200,
  TQueryData = ListOrganicTrafficSeriesStatus200,
  TQueryKey extends QueryKey = ListOrganicTrafficSeriesQueryKey,
>(
  {
    query,
  }: {
    query:
      | ListOrganicTrafficSeriesOptions['query']
      | (() => ListOrganicTrafficSeriesOptions['query'])
  },
  options: {
    query?: Partial<
      QueryObserverOptions<
        ListOrganicTrafficSeriesStatus200,
        ResponseErrorConfig<
          | ListOrganicTrafficSeriesStatus400
          | ListOrganicTrafficSeriesStatus403
          | ListOrganicTrafficSeriesStatus500
        >,
        TData,
        TQueryData,
        TQueryKey
      >
    > & { client?: QueryClient }
    client?: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>>
  } = {},
) {
  const { query: queryConfig = {}, client: config = {} } = options ?? {}
  const { client: queryClient, ...resolvedOptions } = queryConfig
  const resolvedParams = { query: typeof query === 'function' ? query() : query }
  const queryKey = resolvedOptions?.queryKey ?? listOrganicTrafficSeriesQueryKey(resolvedParams)

  const queryResult = useQuery(
    {
      ...listOrganicTrafficSeriesQueryOptions(resolvedParams, config),
      ...resolvedOptions,
      queryKey,
    } as unknown as QueryObserverOptions,
    queryClient,
  ) as UseQueryResult<
    TData,
    ResponseErrorConfig<
      | ListOrganicTrafficSeriesStatus400
      | ListOrganicTrafficSeriesStatus403
      | ListOrganicTrafficSeriesStatus500
    >
  > & { queryKey: TQueryKey }

  queryResult.queryKey = queryKey as TQueryKey

  return queryResult
}
